import type {
  ClassificationTypes,
  automotiveClassificationLabels,
  automotiveClassificationTypesLabels,
  automotiveClassificationTypesValues,
  automotiveClassificationValues
} from "@dealer-center/ddc-pages-inventory-config-interface";
import { automotiveClassificationGetValue } from "@dealer-center/ddc-pages-inventory-config-interface";
import type { classificationInputs, updateValuesInterface } from "..";
import { configItemContext } from "..";
import type {
  CheckBoxEventValue,
  InterstateOnChangeEvent
} from "@dealer-center/core-components";
import {
  CheckBox,
  Grid,
  RadioButtonList
} from "@dealer-center/core-components";
import { useContext } from "react";
import {
  StyledIconInfoOutline,
  StyledImport,
  StyledImportedText,
  StyledInputWrapper
} from "./styles";

interface PagesInventoryConfigUiConfigDispayInputClassificationInterface {
  item: classificationInputs;
}

export const ConfigClassification = ({
  item
}: PagesInventoryConfigUiConfigDispayInputClassificationInterface) => {
  const {
    curentConfig,
    setCurrentConfigValue,
    importedConfig,
    selectedConfig
  } = useContext(configItemContext);
  const currentConfigItem = curentConfig[
    item.dataIndex
  ] as ClassificationTypes[];
  const importedConfigItem = importedConfig?.[
    item.dataIndex
  ] as ClassificationTypes[];
  const selectedConfigItem = selectedConfig?.[
    item.dataIndex
  ] as ClassificationTypes[];
  const isUsingImportedValue =
    compareArrays(currentConfigItem, importedConfigItem) &&
    !compareArrays(currentConfigItem, selectedConfigItem);

  return (
    <StyledInputWrapper>
      <Grid container spacing={2}>
        {item.options.map(option => {
          const {
            selected: isSelected,
            allSelected: areAllSelected,
            selectedSubOpton
          } = getSelected(currentConfigItem, option);
          return (
            <Grid
              data-testid={`Classification-${option.value}`}
              key={option.value}
              size={{ lg: 4, md: 6, sm: 12 }}
              sx={{
                paddingBottom: "1em"
              }}
            >
              <CheckBox
                checked={isSelected}
                disabled={
                  areAllSelected &&
                  option.value !==
                    automotiveClassificationGetValue("All Classes")
                }
                label={option.label}
                sx={{
                  pb: 2
                }}
                value={`Classification-${option.value}`}
                onChange={updateCheckBox(
                  option,
                  selectedSubOpton,
                  setCurrentConfigValue,
                  item,
                  currentConfigItem
                )}
              />
              <RadioButtonList.RadioButtonList
                inline
                displayLabel={false}
                layout="vertical"
                name={`Classification-${option.value}-sub`}
                options={option.options.map(subOption => ({
                  label: subOption.label,
                  value: subOption.value.toString(),
                  disabled: !isSelected
                }))}
                sx={{
                  "& .MuiFormGroup-root": {
                    gap: "1vw"
                  }
                }}
                value={selectedSubOpton}
                onChange={updateRadioButtons(
                  option,
                  setCurrentConfigValue,
                  item,
                  currentConfigItem
                )}
              />
            </Grid>
          );
        })}
      </Grid>
      {isUsingImportedValue && (
        <StyledImport>
          <StyledIconInfoOutline title="Using imported value" />
          <StyledImportedText>Using imported value</StyledImportedText>
        </StyledImport>
      )}
    </StyledInputWrapper>
  );
};

const getSelected = (
  currentConfigItem: ClassificationTypes[],
  option: Option
) => {
  const isSelected =
    currentConfigItem.find(item => item.classification === option.value) !==
    undefined;
  const selectedSubOpton =
    currentConfigItem
      .find(item => item.classification === option.value)
      ?.type?.toString() || "";
  const isAllSelected =
    currentConfigItem.find(
      item =>
        item.classification === automotiveClassificationGetValue("All Classes")
    ) !== undefined;

  return { selected: isSelected, allSelected: isAllSelected, selectedSubOpton };
};

const updateRadioButtons =
  (
    option: Option,
    updateValues: (config: updateValuesInterface) => void,
    item: classificationInputs,
    currentConfigItem: ClassificationTypes[]
  ) =>
  (e: InterstateOnChangeEvent<RadioButtonList.RadioButtonEventValue>) => {
    const newClassificationTypeList: ClassificationTypes = {
      classification: option.value,
      type: parseInt(
        e.target.value || "",
        10
      ) as automotiveClassificationTypesValues
    };

    const currentConfigWithoutCurrentItem = currentConfigItem.filter(
      item => item.classification !== option.value
    );

    updateValues({
      key: item.dataIndex,
      value: [...currentConfigWithoutCurrentItem, newClassificationTypeList]
    });
  };

const updateCheckBox =
  (
    option: Option,
    selectedSubOpton: string,
    updateValues: (config: updateValuesInterface) => void,
    item: classificationInputs,
    currentConfigItem: ClassificationTypes[]
  ) =>
  (e: InterstateOnChangeEvent<CheckBoxEventValue>) => {
    const newClassificationTypeList: ClassificationTypes[] = [
      {
        classification: option.value,
        type: parseInt(
          selectedSubOpton,
          10
        ) as automotiveClassificationTypesValues
      }
    ];
    if (e.target.value?.checked) {
      if (option.value === automotiveClassificationGetValue("All Classes")) {
        updateValues({
          key: item.dataIndex,
          value: newClassificationTypeList
        });
        return;
      }
      updateValues({
        key: item.dataIndex,
        value: [...currentConfigItem, ...newClassificationTypeList]
      });
    } else {
      const newClassificationTypeList = currentConfigItem.filter(
        item => item.classification !== option.value
      );
      updateValues({
        key: item.dataIndex,
        value: [...newClassificationTypeList]
      });
    }
  };

interface Option {
  label: automotiveClassificationLabels;
  value: automotiveClassificationValues;
  options: {
    label: automotiveClassificationTypesLabels;
    value: automotiveClassificationTypesValues;
  }[];
}

const compareArrays = (
  arr1?: ClassificationTypes[],
  arr2?: ClassificationTypes[]
) => {
  if (!arr1 || !arr2) {
    return false;
  }
  if (arr1.length !== arr2.length) {
    return false;
  }
  for (let i = arr1.length; i--; ) {
    if (arr1[i] !== arr2[i]) {
      return false;
    }
  }
  return true;
};
